import { lineOf, atSlot, type BoardKind } from './ai/board';
import { balance, type Balance } from './balance';
import { STRENGTH, battleMap, referenceMaxHp, type BattleStat, type BattleStats } from './matchup';
import { DEFENSE_SLOTS, OFFENSE_SLOTS, SLOT_POSITION } from './playdef';
import { statsAtLevel } from './stats';
import { itemOf } from './subs';
import type {
  Combatant,
  Creature,
  DefenseSlot,
  MatchupKind,
  Move,
  OffenseSlot,
  Position,
  Team,
} from './types';

/**
 * Roster building (slice 11, the Quick Play roster screen). Read-only helpers over the sim's own
 * rules: nothing here changes how a game plays. A pack Creature becomes a level-50 Combatant; each
 * slot gets a grade from the stat it fights with, as a battle computes it (position fit
 * included); the proposal charts the picks to the best total; and a team is checked before a game.
 * Every number is balance.json `pvp.level`, `roster`, `status.itemBagPerGame`.
 */

export type RosterSlot = OffenseSlot | DefenseSlot;
export type Grade = 'A' | 'B' | 'C' | 'D';
/**
 * The stat a battle side reads, as a key: one stat (`spe`), or two averaged named together
 * (`atkDef` is avg(Atk, Def)). Slice 10g: whatever balance.json `battles` lists.
 */
export type GradeStat = BattleStat | `${BattleStat}${Capitalize<BattleStat>}`;

/** The key for a side's stats: `['atk', 'def']` is `atkDef`. */
export function statKey(stats: BattleStats): GradeStat {
  const [x, y] = stats;
  return y === undefined
    ? x
    : `${x}${(y.charAt(0).toUpperCase() + y.slice(1)) as Capitalize<BattleStat>}`;
}

/** The stats a key names, in order: `atkDef` is `['atk', 'def']`. */
export function statsOf(key: GradeStat): BattleStats {
  const m = /^([a-z]{3})([A-Z][a-z]{2})?$/.exec(key);
  if (!m) throw new Error(`not a stat key: ${key}`);
  const x = m[1] as BattleStat;
  return m[2] ? [x, m[2].toLowerCase() as BattleStat] : [x];
}

/** The stat one side of a battle of `kind` reads (balance.json `battles`). */
export function sideStat(
  kind: MatchupKind,
  side: 'offense' | 'defense',
  b: Balance = balance,
): GradeStat {
  return statKey(battleMap(b)[kind][side]);
}

export interface SlotGrade {
  grade: Grade;
  stat: GradeStat;
  /** The stat as it plays in this slot at full HP with no stages: position fit applied. */
  value: number;
  /** The position-fit multiplier in this slot (1 in position). */
  fit: number;
}

/** A team on the roster screen: charts may still have empty slots. */
export interface TeamDraft extends Omit<Team, 'offense' | 'defense'> {
  offense: Partial<Record<OffenseSlot, number>>;
  defense: Partial<Record<DefenseSlot, number>>;
}

/** A pack creature as a fresh level-`pvp.level` Combatant: full HP and PP, no stages, no ability. */
export function combatantOf(
  creature: Pick<Creature, 'id' | 'name' | 'types' | 'baseStats'>,
  moves: readonly Move[],
  positions?: readonly Position[],
  b: Balance = balance,
): Combatant {
  const stats = statsAtLevel(creature.baseStats, b.pvp.level);
  return {
    creatureId: creature.id,
    name: creature.name,
    types: creature.types,
    level: b.pvp.level,
    stats,
    position: positions?.[0] ?? 'BENCH',
    ...(positions ? { positions: [...positions] } : {}),
    moves: [...moves],
    ppLeft: Object.fromEntries(moves.map((m) => [m.id, m.pp])),
    hp: stats.hp,
    stages: {},
    enteredThisPlay: false,
  };
}

/** Which stat the slot's battle reads, from its board line and side (balance.json `battles`). */
export function gradeStat(slot: RosterSlot, b: Balance = balance): GradeStat {
  const line = lineOf(slot);
  return sideStat(line.kind, line.offense === slot ? 'offense' : 'defense', b);
}

/** `c` fresh in `slot`: full HP, no status or stages, the slot's position. */
function fresh(c: Combatant, slot: RosterSlot): Combatant {
  const out: Combatant = { ...atSlot(c, slot, true), hp: c.stats.hp };
  delete out.status;
  return out;
}

/** The stat `c` fights with in `slot`: its board line's STRENGTH side, as a battle computes it. */
export function slotValue(c: Combatant, slot: RosterSlot, b: Balance): number {
  const line = lineOf(slot);
  const side = STRENGTH[line.kind];
  return (line.offense === slot ? side.a : side.b)(fresh(c, slot), b);
}

/** The letters' bars on one side of a board line, in level-`pvp.level` stat units. */
export interface GradeThresholds {
  A: number;
  B: number;
  C: number;
}

/**
 * The thresholds one side of a board line is graded against (slice 10i): balance.json
 * `roster.grades.thresholds.<kind>.<side>`, written by `pnpm grades` so that each letter means the
 * share of fresh, neutral, moveless battles in `roster.grades.shares` won against the field. The
 * sim never derives them: it looks them up.
 */
export function gradeThresholds(
  kind: BoardKind,
  side: 'offense' | 'defense',
  b: Balance,
): GradeThresholds {
  const t = b.roster.grades.thresholds[kind]?.[side];
  if (!t) throw new Error(`balance.json roster.grades.thresholds.${kind}.${side} is missing`);
  return t;
}

/** The thresholds `slot` is graded against: its board line's kind and side. */
export function slotThresholds(slot: RosterSlot, b: Balance = balance): GradeThresholds {
  const line = lineOf(slot);
  return gradeThresholds(line.kind, line.offense === slot ? 'offense' : 'defense', b);
}

/** A value's letter on one side of a board line; a value exactly at a threshold is that letter. */
export function gradeOf(
  value: number,
  kind: BoardKind,
  side: 'offense' | 'defense',
  b: Balance = balance,
): Grade {
  const t = gradeThresholds(kind, side, b);
  if (value >= t.A) return 'A';
  if (value >= t.B) return 'B';
  if (value >= t.C) return 'C';
  return 'D';
}

/**
 * Stamina, the second mark (slice 10i): how many lost battles a Pokémon with `maxHp` lasts before
 * it tires (falls under `hp.fatigueThreshold` of its max), at the measured mean a lost battle costs
 * in the games the player plays (`roster.stamina.lossPerLostBattle`, points). Display only: no rule
 * reads it; the sheet's HP bar and the Lineup sub-lines say it.
 */
export function lostBattlesBeforeTired(maxHp: number, b: Balance = balance): number {
  const loss = b.roster.stamina.lossPerLostBattle;
  if (!(loss > 0)) throw new Error('balance.json roster.stamina.lossPerLostBattle must be above 0');
  return Math.floor((b.hp.fatigueThreshold * maxHp) / loss);
}

/** What the reference Pokémon (`hp.referenceBase`) lasts: the scale beside a Pokémon's own count. */
export function referenceStamina(b: Balance = balance): number {
  return lostBattlesBeforeTired(referenceMaxHp(b), b);
}

export function slotGrade(c: Combatant, slot: RosterSlot, b: Balance = balance): SlotGrade {
  const value = slotValue(c, slot, b);
  const position = atSlot(c, slot).position;
  const fit = !c.positions || c.positions.includes(position) ? 1 : fitOf(position, b);
  const line = lineOf(slot);
  const side = line.offense === slot ? 'offense' : 'defense';
  return { grade: gradeOf(value, line.kind, side, b), stat: gradeStat(slot, b), value, fit };
}

export interface BenchGrade extends SlotGrade {
  /** The slot that grade is for, and its position ("ATK + DEF: B at OL"). */
  slot: RosterSlot;
  position: Position;
}

const GRADE_RANK: Record<Grade, number> = { A: 3, B: 2, C: 1, D: 0 };

/**
 * A bench Pokémon's best grade on one side (slice 11c, the Chart tab's bench rows): the best
 * `slotGrade` over every slot on that side, position fit included, so a lineman graded C at OL
 * out of position beats a D at the TE he lists. By grade; on a tie in grade a slot it lists a
 * position for wins, then the higher value, then the earlier slot.
 */
export function benchGrade(
  c: Combatant,
  side: 'offense' | 'defense',
  b: Balance = balance,
): BenchGrade {
  const slots: readonly RosterSlot[] = side === 'offense' ? OFFENSE_SLOTS : DEFENSE_SLOTS;
  const lists = (s: RosterSlot) => !c.positions || c.positions.includes(SLOT_POSITION[s]);
  /** Grade, then listed, then value: compared in that order, the first difference decides. */
  const key = (g: SlotGrade, s: RosterSlot) => [GRADE_RANK[g.grade], Number(lists(s)), g.value];
  let best: { g: SlotGrade; slot: RosterSlot; key: number[] } | undefined;
  for (const slot of slots) {
    const g = slotGrade(c, slot, b);
    const k = key(g, slot);
    const d = best ? k.map((x, i) => x - (best?.key[i] ?? 0)).find((x) => x !== 0) : 1;
    if (d !== undefined && d > 0) best = { g, slot, key: k };
  }
  if (!best) throw new Error('a side has no slots');
  return { ...best.g, slot: best.slot, position: SLOT_POSITION[best.slot] };
}

function fitOf(position: Position, b: Balance): number {
  const { families, mult } = b.positionFit;
  for (const [family, members] of Object.entries(families)) {
    if ((members as readonly string[]).includes(position)) {
      return (mult as Record<string, number>)[family] ?? 1;
    }
  }
  return 1;
}

/**
 * One chart: the assignment of roster indices to `slots` with the highest total slot value, each
 * Pokémon at most once. Exact (a DP over slots and the set of Pokémon used). With fewer Pokémon
 * than slots, as many slots as there are Pokémon are filled. Ties go to the lower roster index in
 * the earlier slot, so the proposal is stable.
 */
export function proposeChart<S extends RosterSlot>(
  roster: readonly Combatant[],
  slots: readonly S[],
  b: Balance,
): Partial<Record<S, number>> {
  const n = roster.length;
  const k = slots.length;
  const value = roster.map((c) => slots.map((s) => slotValue(c, s, b)));
  const bits = (m: number) => {
    let x = 0;
    for (let v = m; v; v &= v - 1) x += 1;
    return x;
  };
  // best[j][mask]: the best total for slots j.. with the Pokémon in `mask` already used.
  const size = 1 << n;
  const best: Float64Array[] = Array.from({ length: k + 1 }, () => new Float64Array(size));
  const pick: Int16Array[] = Array.from({ length: k }, () => new Int16Array(size));
  for (let j = k - 1; j >= 0; j -= 1) {
    for (let mask = 0; mask < size; mask += 1) {
      let top = -Infinity;
      let choice = -1;
      // Leaving a slot empty only when the Pokémon left cannot fill every slot left.
      if (k - j > n - bits(mask)) {
        top = (best[j + 1] as Float64Array)[mask] as number;
      }
      for (let i = 0; i < n; i += 1) {
        if (mask & (1 << i)) continue;
        const v =
          (value[i]?.[j] as number) + ((best[j + 1] as Float64Array)[mask | (1 << i)] as number);
        if (v > top) {
          top = v;
          choice = i;
        }
      }
      (best[j] as Float64Array)[mask] = top;
      (pick[j] as Int16Array)[mask] = choice;
    }
  }
  const chart: Partial<Record<S, number>> = {};
  let mask = 0;
  for (let j = 0; j < k; j += 1) {
    const i = (pick[j] as Int16Array)[mask] as number;
    if (i < 0) continue;
    chart[slots[j] as S] = i;
    mask |= 1 << i;
  }
  return chart;
}

/** Both charts, proposed independently: a Pokémon may start both ways, as the sim allows. */
export function proposeCharts(
  roster: readonly Combatant[],
  b: Balance = balance,
): Pick<TeamDraft, 'offense' | 'defense'> {
  return {
    offense: proposeChart(roster, OFFENSE_SLOTS, b),
    defense: proposeChart(roster, DEFENSE_SLOTS, b),
  };
}

export type RosterProblem =
  | { code: 'count'; have: number; need: number }
  | { code: 'species'; index: number }
  | { code: 'empty-slot'; side: 'offense' | 'defense'; slot: RosterSlot }
  | { code: 'twice'; side: 'offense' | 'defense'; index: number }
  | { code: 'moves'; index: number; count: number }
  | { code: 'move-twice'; index: number; moveId: string }
  | { code: 'not-learned'; index: number; moveId: string }
  | { code: 'bag-size'; have: number; max: number }
  | { code: 'bag-item'; item: string }
  | { code: 'item-clause'; item: string };

/**
 * Everything wrong with a team before a game, in roster order (an empty list is a legal team).
 * Beyond what `newGame` checks: exactly `roster.starters + roster.bench` Pokémon, Species Clause,
 * 1-4 moves each, every move in its learnset, and Item Clause on the bag and held items.
 */
export function rosterProblems(
  team: TeamDraft,
  learnsetOf: (creatureId: string) => readonly string[] | undefined,
  b: Balance = balance,
): RosterProblem[] {
  const out: RosterProblem[] = [];
  const need = b.roster.starters + b.roster.bench;
  const n = team.roster.length;
  if (n !== need) out.push({ code: 'count', have: n, need });
  const seen = new Set<string>();
  team.roster.forEach((c, index) => {
    if (seen.has(c.creatureId)) out.push({ code: 'species', index });
    seen.add(c.creatureId);
  });
  for (const [side, slots] of [
    ['offense', OFFENSE_SLOTS],
    ['defense', DEFENSE_SLOTS],
  ] as const) {
    const chart = team[side] as Partial<Record<RosterSlot, number>>;
    const used = new Set<number>();
    for (const slot of slots) {
      const i = chart[slot];
      if (i === undefined || !Number.isInteger(i) || i < 0 || i >= n) {
        out.push({ code: 'empty-slot', side, slot });
        continue;
      }
      if (used.has(i)) out.push({ code: 'twice', side, index: i });
      used.add(i);
    }
  }
  team.roster.forEach((c, index) => {
    if (c.moves.length < 1 || c.moves.length > 4) {
      out.push({ code: 'moves', index, count: c.moves.length });
    }
    const learnset = new Set(learnsetOf(c.creatureId) ?? []);
    const known = new Set<string>();
    for (const m of c.moves) {
      if (known.has(m.id)) out.push({ code: 'move-twice', index, moveId: m.id });
      known.add(m.id);
      if (!learnset.has(m.id)) out.push({ code: 'not-learned', index, moveId: m.id });
    }
  });
  const bag = team.bag ?? [];
  if (bag.length > b.status.itemBagPerGame) {
    out.push({ code: 'bag-size', have: bag.length, max: b.status.itemBagPerGame });
  }
  const items = new Set<string>();
  for (const item of bag) {
    if (itemOf(item, b)?.slot !== 'bag') out.push({ code: 'bag-item', item });
    if (items.has(item)) out.push({ code: 'item-clause', item });
    items.add(item);
  }
  for (const c of team.roster) {
    if (!c.itemId) continue;
    if (items.has(c.itemId)) out.push({ code: 'item-clause', item: c.itemId });
    items.add(c.itemId);
  }
  return out;
}
